import React from 'react';
import { Chip, CircularProgress, makeStyles } from '@material-ui/core';
import { StandardTable } from './table';

/**
 * Column definition for ResponsiveDataView.
 *
 * - `key` is a stable identifier (used for React keys + column visibility).
 * - `render` lets a column produce arbitrary JSX from a row. If omitted the
 *   string indexer `row[key]` is used as the cell value.
 * - `hideWhenEmpty` drops the column when no row in the current page has a
 *   non-empty value, so optional fields don't waste horizontal space.
 * - `primary` marks the row's main column (gets the bold treatment + the
 *   "Inactive" badge when the row is de-emphasized).
 * - `headerRender` replaces the default <th>{label}</th> — use for sortable
 *   headers (TableCellWithSort) or any other interactive header.
 */
export type Column<T> = {
    key: string;
    label: string;
    render?: (row: T) => React.ReactNode;
    hideWhenEmpty?: boolean;
    primary?: boolean;
    headerRender?: () => React.ReactNode;
};

type Props<T> = {
    rows: T[];
    columns: Column<T>[];
    rowKey: (row: T) => string | number;
    actions?: (row: T) => React.ReactNode;
    /** Visual de-emphasis (opacity + Inactive badge in primary cell). */
    isInactive?: (row: T) => boolean;
    /** Rendered when `rows` is empty. */
    emptyState?: React.ReactNode;
    /** When set, makes whole row clickable. */
    onRowClick?: (row: T) => void;
    /**
     * When the caller is still fetching data, the empty-state placeholder is
     * suppressed and a spinner is shown instead. Prevents the "No results"
     * message flashing before the first response arrives (Elen QA on PAS-914).
     */
    loading?: boolean;
};

const defaultEmpty = <div>No results.</div>;
const defaultLoading = <div style={{ padding: '20px', textAlign: 'center' }}><CircularProgress size={24} /></div>;

/**
 * Single HTML table wrapped in an `overflow-x: auto` container — when the
 * table is wider than its viewport the user gets a horizontal scrollbar
 * rather than clipped content. Optional / empty columns auto-hide.
 */
export function ResponsiveDataView<T>({
    rows,
    columns,
    rowKey,
    actions,
    isInactive,
    emptyState = defaultEmpty,
    onRowClick,
    loading,
}: Props<T>) {
    const classes = useStyles();

    if (rows.length === 0) return <>{loading ? defaultLoading : emptyState}</>;

    const visibleColumns = columns.filter((col) => {
        if (!col.hideWhenEmpty) return true;
        return rows.some((row) => {
            if (col.render) return !!col.render(row);
            const v = (row as unknown as Record<string, unknown>)[col.key];
            return v !== undefined && v !== null && String(v).trim() !== '';
        });
    });

    const primaryCol = columns.find((c) => c.primary) ?? columns[0];

    return (
        <div className={classes.tableScroll}>
            <StandardTable>
                <thead>
                    <tr>
                        {visibleColumns.map((c) =>
                            c.headerRender ? (
                                <React.Fragment key={c.key}>{c.headerRender()}</React.Fragment>
                            ) : (
                                <th key={c.key}>{c.label}</th>
                            )
                        )}
                        {actions && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const inactive = isInactive?.(row);
                        return (
                            <tr
                                key={rowKey(row)}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={[
                                    inactive ? classes.rowInactive : '',
                                    onRowClick ? classes.rowClickable : '',
                                ].filter(Boolean).join(' ') || undefined}
                            >
                                {visibleColumns.map((c) => {
                                    const value = c.render
                                        ? c.render(row)
                                        : (row as unknown as Record<string, React.ReactNode>)[c.key];
                                    if (c === primaryCol && inactive) {
                                        return (
                                            <td key={c.key}>
                                                <div className={classes.primaryCell}>
                                                    <span>{value}</span>
                                                    <Chip size="small" label="Inactive" className={classes.inactiveBadge} />
                                                </div>
                                            </td>
                                        );
                                    }
                                    return <td key={c.key}>{value}</td>;
                                })}
                                {actions && (
                                    <td>
                                        <div className={classes.actionsCell}>{actions(row)}</div>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </StandardTable>
        </div>
    );
}

const useStyles = makeStyles((theme) => ({
    tableScroll: {
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    rowInactive: {
        opacity: 0.65,
    },
    rowClickable: {
        cursor: 'pointer',
        '&:hover td': {
            backgroundColor: theme.palette.grey[100],
        },
    },
    inactiveBadge: {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.text.secondary,
        height: '20px',
        fontSize: '11px',
    },
    primaryCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    actionsCell: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
}));
